/**
 * Main Backstage application using the new frontend system.
 *
 * This file configures the IntelliPact Admin Portal using the native new frontend
 * system architecture with plugin alpha exports and extension blueprints.
 *
 * @packageDocumentation
 */

import { createApp } from '@backstage/frontend-defaults';
import {
  SignInPageBlueprint,
  AppRootWrapperBlueprint,
} from '@backstage/plugin-app-react';
import {
  ApiBlueprint,
  createFrontendModule,
  createApiFactory,
  createFrontendFeatureLoader,
  configApiRef,
  microsoftAuthApiRef,
  type ConfigApi,
  type ExtensionDefinition,
  type FrontendFeature,
} from '@backstage/frontend-plugin-api';

import { SignInPage } from '@backstage/core-components';
import { ResilientAutoLogout } from './components/auth';
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
} from '@backstage/integration-react';

// Import alpha exports from core Backstage plugins required in every shell.
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import searchPlugin from '@backstage/plugin-search/alpha';
import userSettingsPlugin from '@backstage/plugin-user-settings/alpha';

// Custom tenant management plugin (alpha export)
import tenantManagementPlugin from '@internal/plugin-tenant-management/alpha';

// Custom extensions required in every shell.
import { navExtensions } from './extensions/nav';
import { homepageExtensions } from './extensions/homepage';
import { settingsExtensions } from './extensions/settings';
import { BrowserTelemetryInitializer } from './instrumentation/BrowserTelemetryInitializer';

function createCorrelationId(): string {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `frontend-${Date.now().toString(36)}`;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function getErrorStack(error: unknown): string | undefined {
  return error instanceof Error ? error.stack : undefined;
}

function logStructuredFrontendError(
  message: string,
  error: unknown,
  attributes: Record<string, unknown> = {},
) {
  console.error(
    JSON.stringify({
      timestamp: new Date().toISOString(),
      level: 'error',
      message,
      correlationId: createCorrelationId(),
      errorMessage: getErrorMessage(error),
      errorStack: getErrorStack(error),
      ...attributes,
    }),
  );
}

/**
 * SCM Integrations API extension.
 * Provides integration with source control management systems.
 */
const scmIntegrationsApi: ExtensionDefinition = ApiBlueprint.make({
  name: 'scm-integrations',
  params: defineParams =>
    defineParams(
      createApiFactory({
        api: scmIntegrationsApiRef,
        deps: { configApi: configApiRef },
        factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
      }),
    ),
});

/**
 * AutoLogout wrapper extension for idle session timeout.
 * Wraps the entire app to track user activity and log out after inactivity.
 * Configuration is read from app-config.yaml auth.autologout section.
 *
 * Uses ResilientAutoLogout wrapper to handle signOut errors gracefully.
 * When signOut() fails (e.g., 401 due to expired session), the wrapper
 * detects this and forces a redirect to the sign-in page.
 *
 * See: https://backstage.io/docs/auth/autologout/
 */
const autoLogoutWrapper: ExtensionDefinition = AppRootWrapperBlueprint.make({
  name: 'auto-logout',
  params: {
    component: ({ children }) => (
      <>
        <ResilientAutoLogout />
        {children}
      </>
    ),
  },
});

const browserTelemetryWrapper: ExtensionDefinition =
  AppRootWrapperBlueprint.make({
    name: 'browser-telemetry',
    params: {
      component: ({ children }) => (
        <>
          <BrowserTelemetryInitializer />
          {children}
        </>
      ),
    },
  });

/**
 * Sign-in page extension with Microsoft Entra ID provider.
 * Configures authentication for IntelliPact Admin Portal.
 */
const signInPage: ExtensionDefinition = SignInPageBlueprint.make({
  params: {
    loader: async () => props => (
      <SignInPage
        {...props}
        providers={[
          {
            id: 'microsoft',
            title: 'Microsoft',
            message: 'Sign in using Microsoft Entra ID',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    ),
  },
});

/**
 * App module containing custom app-level extensions.
 * Bundles sign-in, navigation, settings, and session management extensions.
 */
const appExtensions: ExtensionDefinition[] = [
  signInPage,
  scmIntegrationsApi,
  autoLogoutWrapper,
  browserTelemetryWrapper,
  ...navExtensions,
  ...homepageExtensions,
  ...settingsExtensions,
];

const appModule = createFrontendModule({
  pluginId: 'app',
  extensions: appExtensions,
});

function isOptionalFeatureEnabled(config: ConfigApi, feature: string): boolean {
  return (
    config.getOptionalBoolean(`app.optionalFeatures.${feature}.enabled`) ?? true
  );
}

interface OptionalFeatureLoader {
  feature: string;
  requiredFeatures?: string[];
  load: () => Promise<FrontendFeature>;
}

/**
 * Lazily-loaded optional feature surfaces, gated by
 * `app.optionalFeatures.<name>.enabled`.
 *
 * Exported so tests can exercise every `load()` directly. The app only reaches
 * these imports when a feature is enabled, and several resolve plugin
 * extensions by string id at import time, which TypeScript cannot verify.
 */
export const optionalFeatureLoaders: OptionalFeatureLoader[] = [
  {
    feature: 'catalogImport',
    load: async () =>
      (await import('@backstage/plugin-catalog-import/alpha')).default,
  },
  {
    feature: 'scaffolder',
    load: async () =>
      (await import('@backstage/plugin-scaffolder/alpha')).default,
  },
  {
    feature: 'techdocs',
    load: async () =>
      (await import('@backstage/plugin-techdocs/alpha')).default,
  },
  {
    feature: 'apiDocs',
    load: async () =>
      (await import('@backstage/plugin-api-docs/alpha')).default,
  },
  {
    feature: 'notifications',
    load: async () =>
      (await import('@backstage/plugin-notifications/alpha')).default,
  },
  {
    feature: 'signals',
    load: async () => (await import('@backstage/plugin-signals/alpha')).default,
  },
  {
    feature: 'org',
    load: async () => (await import('@backstage/plugin-org/alpha')).default,
  },
  {
    feature: 'catalogGraph',
    load: async () =>
      (await import('@backstage/plugin-catalog-graph/alpha')).default,
  },
  {
    feature: 'costInsights',
    load: async () =>
      (await import('./extensions/cost-insights')).costInsightsPlugin,
  },
  {
    feature: 'rbac',
    load: async () =>
      (await import('@backstage-community/plugin-rbac')).default,
  },
  {
    feature: 'scaffolderFieldExtensions',
    requiredFeatures: ['scaffolder'],
    load: async () =>
      (await import('./extensions/scaffolder')).scaffolderFieldExtensions,
  },
  {
    feature: 'catalogExtensions',
    load: async () => (await import('./extensions/catalog')).catalogExtensions,
  },
];

const optionalFeaturesLoader = createFrontendFeatureLoader({
  async *loader({ config }) {
    for (const {
      feature,
      requiredFeatures = [],
      load,
    } of optionalFeatureLoaders) {
      if (
        !isOptionalFeatureEnabled(config, feature) ||
        !requiredFeatures.every(requiredFeature =>
          isOptionalFeatureEnabled(config, requiredFeature),
        )
      ) {
        continue;
      }

      try {
        yield await load();
      } catch (error) {
        const message = `Invalid APP_CONFIG: enabled optional frontend feature "${feature}" failed to load`;
        logStructuredFrontendError(message, error, { feature });
        throw new Error(message, { cause: error });
      }
    }
  },
});

export function createAdminPortalAppRoot() {
  /**
   * Create the Backstage app using the new frontend system.
   * All plugins use their alpha exports for native integration.
   */
  const app = createApp({
    features: [
      // Core Backstage plugins (alpha exports)
      catalogPlugin,
      searchPlugin,
      userSettingsPlugin,

      // Custom plugins
      tenantManagementPlugin,

      // App-level module with custom extensions
      appModule,

      // Optional feature surfaces. Keep enabled by default; disable via
      // app.optionalFeatures.<name>.enabled to keep disabled Backstage routes
      // out of the default app shell.
      optionalFeaturesLoader,
    ],
  });

  return app.createRoot();
}

export default createAdminPortalAppRoot;
